import { Mail, MapPin, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';

interface FooterProps {
  onOpenPrivacySettings: () => void;
}

const CATEGORY_LINKS = [
  { label: 'Cars & Vehicles', slug: 'cars-vehicles' },
  { label: 'Property', slug: 'property' },
  { label: 'Mobiles', slug: 'mobile-phones' },
  { label: 'Jobs', slug: 'jobs' },
  { label: 'Bikes', slug: 'motorbikes-scooters' },
];

const COMPANY_LINKS = [
  { label: 'About Us', to: '/about' },
  { label: 'Careers', to: '/careers' },
  { label: 'Contact', to: '/contact' },
];

const SUPPORT_LINKS = [
  { label: 'Help Center', to: '/help-center' },
  { label: 'Safety Tips', to: '/safety-tips' },
  { label: 'Report Ad', to: '/report-ad' },
  { label: 'Terms', to: '/terms' },
  { label: 'Privacy', to: '/privacy' },
];

export default function Footer({ onOpenPrivacySettings }: FooterProps): JSX.Element {
  return (
    <footer className="border-t border-dark-800 bg-dark-950">
      <div className="mx-auto max-w-7xl px-4 pb-8 pt-16 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-4">
            <Link to="/" aria-label="AdsWorld Home" className="inline-flex items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-dark-950">
              <img src="/adsworld-logo-transparent.svg" alt="AdsWorld Logo" className="h-13 w-auto object-contain" />
            </Link>
            <p className="mt-6 max-w-xs text-sm leading-relaxed text-dark-300">
              Buy and sell cars, property, mobiles, jobs, and more in Pakistan. A simple marketplace connecting local buyers and sellers.
            </p>
            <address className="mt-6 space-y-3 not-italic">
              <a href="mailto:support@adsworld24.com" className="flex min-h-11 items-center gap-2 text-sm text-dark-200 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-dark-950">
                <Mail className="h-4 w-4" aria-hidden="true" />
                support@adsworld24.com
              </a>
              <a href="tel:+923024333398" className="flex min-h-11 items-center gap-2 text-sm text-dark-200 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-dark-950">
                <Phone className="h-4 w-4" aria-hidden="true" />
                +92 302 4333398
              </a>
              <p className="flex min-h-11 items-center gap-2 text-sm text-dark-200">
                <MapPin className="h-4 w-4" aria-hidden="true" />
                Pakistan
              </p>
            </address>
          </div>

          <nav aria-label="Categories" className="lg:col-span-2">
            <h2 className="mb-4 text-sm font-semibold text-white">Categories</h2>
            <ul className="space-y-1">
              {CATEGORY_LINKS.map((link) => (
                <li key={link.slug}>
                  <Link to={`/category/${link.slug}`} className="footer-link min-h-11 py-2">{link.label}</Link>
                </li>
              ))}
            </ul>
          </nav>

          <nav aria-label="Company" className="lg:col-span-2">
            <h2 className="mb-4 text-sm font-semibold text-white">Company</h2>
            <ul className="space-y-1">
              {COMPANY_LINKS.map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className="footer-link min-h-11 py-2">{link.label}</Link>
                </li>
              ))}
            </ul>
          </nav>

          <nav aria-label="Support" className="lg:col-span-2">
            <h2 className="mb-4 text-sm font-semibold text-white">Support</h2>
            <ul className="space-y-1">
              {SUPPORT_LINKS.map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className="footer-link min-h-11 py-2">{link.label}</Link>
                </li>
              ))}
            </ul>
          </nav>

          <div className="lg:col-span-2">
            <h2 className="mb-4 text-sm font-semibold text-white">Mobile app</h2>
            <p className="text-sm leading-relaxed text-dark-300">Our mobile apps are not available yet. Browse and post securely from this website.</p>
          </div>
        </div>

        <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-dark-800 pt-8 sm:flex-row">
          <p className="text-sm text-dark-300">Copyright 2026 AdsWorld. Built for Pakistan.</p>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm">
            <Link to="/privacy" className="min-h-11 py-2 text-dark-200 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400">Privacy Policy</Link>
            <button type="button" onClick={onOpenPrivacySettings} className="min-h-11 py-2 text-dark-200 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400">Privacy Settings</button>
            <Link to="/terms" className="min-h-11 py-2 text-dark-200 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400">Terms of Service</Link>
            <Link to="/sitemap" className="min-h-11 py-2 text-dark-200 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400">Sitemap</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
